import { useEffect, useState } from 'react';
import { useStore } from '../state/store';
import { TopBar } from '../components/TopBar';
import { MoneyInput } from '../components/MoneyInput';
import { PctInput } from '../components/PctInput';
import { PrivacyBanner } from '../components/PrivacyBanner';
import { GlossaryTerm } from '../components/GlossaryTerm';
import type { Assumptions, Compensation, PayMonth } from '../types';
import { fmtMoney } from '../utils/format';
import { deriveRequiredSpend } from '../engine/projection';

const DEFAULT_COMPENSATION: Compensation = {
  baseAnnual: 0,
  bonusAnnual: 0,
  bonusMonth: 2,
};

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const DEPRECATED_TOOLTIP =
  'Replaced by Compensation block above; will be removed in a later release.';

export function AssumptionsPage({ onBack }: { onBack: () => void }) {
  const { assumptions, saveAssumptions } = useStore();
  const [draft, setDraft] = useState<Assumptions>(assumptions);
  const [dirty, setDirty] = useState(false);

  useEffect(() => setDraft(assumptions), [assumptions]);

  const required = deriveRequiredSpend(draft);
  const requiredNestEgg = draft.swr > 0 ? required / draft.swr : 0;

  function upd<K extends keyof Assumptions>(k: K, v: Assumptions[K]) {
    setDraft(d => ({ ...d, [k]: v }));
    setDirty(true);
  }

  function updComp<K extends keyof Compensation>(k: K, v: Compensation[K]) {
    setDraft(d => {
      const base = d.compensation ?? { ...DEFAULT_COMPENSATION, baseAnnual: d.grossAnnualSalary };
      return { ...d, compensation: { ...base, [k]: v } };
    });
    setDirty(true);
  }

  const comp = draft.compensation ?? DEFAULT_COMPENSATION;
  const compTotal = comp.baseAnnual + comp.bonusAnnual;

  async function save() {
    await saveAssumptions(draft);
    setDirty(false);
  }

  return (
    <>
      <TopBar
        title="Assumptions"
        subtitle="All $ in today's (real) dollars"
        right={
          <>
            <button className="btn btn-ghost text-sm" onClick={onBack}>
              Back
            </button>
            <button className="btn btn-primary text-sm" disabled={!dirty} onClick={save}>
              Save
            </button>
          </>
        }
      />
      <main className="mx-auto max-w-3xl space-y-4 px-4 py-4 pb-nav md:grid md:max-w-5xl md:grid-cols-2 md:gap-4 md:space-y-0">
        <div className="md:col-span-2">
          <PrivacyBanner />
        </div>
        <Card title="Personal">
          <Row label="Display name">
            <input
              type="text"
              className="input text-right"
              maxLength={60}
              value={draft.displayName ?? ''}
              placeholder="Shown on the Dashboard header"
              onChange={e => upd('displayName', e.target.value)}
            />
          </Row>
          <Row label="Current age">
            <input
              type="number"
              className="input text-right font-mono tabular-nums"
              value={draft.currentAge}
              min={0}
              max={120}
              onChange={e => upd('currentAge', Number(e.target.value))}
            />
          </Row>
          <Row label="Target retirement age">
            <input
              type="number"
              className="input text-right font-mono tabular-nums"
              value={draft.targetRetirementAge}
              min={draft.currentAge}
              max={120}
              onChange={e => upd('targetRetirementAge', Number(e.target.value))}
            />
          </Row>
          <Row label="Starting calendar year">
            <input
              type="number"
              className="input text-right font-mono tabular-nums"
              value={draft.startingCalendarYear}
              onChange={e => upd('startingCalendarYear', Number(e.target.value))}
            />
          </Row>
          <Hint>Years to retirement: {draft.targetRetirementAge - draft.currentAge}</Hint>
        </Card>

        <Card title="Compensation">
          <Row label="Base salary (annual)">
            <MoneyInput value={comp.baseAnnual} onChange={v => updComp('baseAnnual', v)} />
          </Row>
          <Row label="Annual bonus">
            <MoneyInput value={comp.bonusAnnual} onChange={v => updComp('bonusAnnual', v)} />
          </Row>
          <Row label="Bonus month">
            <select
              className="input text-right font-mono tabular-nums"
              value={comp.bonusMonth}
              onChange={e => updComp('bonusMonth', Number(e.target.value) as PayMonth)}
            >
              {MONTH_LABELS.map((label, idx) => (
                <option key={label} value={idx + 1}>
                  {idx + 1} — {label}
                </option>
              ))}
            </select>
          </Row>
          <Row label="Employer 401(k) match">
            <PctInput
              value={comp.employer401kMatchPct ?? 0}
              onChange={v => updComp('employer401kMatchPct', v === 0 ? undefined : v)}
              decimals={2}
            />
          </Row>
          <Row label="Employer match cap (% of pay)">
            <PctInput
              value={comp.employer401kMatchCapPct ?? 0}
              onChange={v => updComp('employer401kMatchCapPct', v === 0 ? undefined : v)}
              decimals={2}
            />
          </Row>
          <Hint>
            Total gross compensation: {fmtMoney(compTotal)}/yr.
            {comp.employer401kMatchPct !== undefined && comp.employer401kMatchCapPct !== undefined && (
              <> Employer matches {(comp.employer401kMatchPct * 100).toFixed(1)}% of pay, capped at {(comp.employer401kMatchCapPct * 100).toFixed(1)}% of pay.</>
            )}
          </Hint>
        </Card>

        <Card title="Income & contributions (legacy)">
          <Row label="Gross annual salary" deprecated>
            <MoneyInput value={draft.grossAnnualSalary} onChange={v => upd('grossAnnualSalary', v)} />
          </Row>
          <Row label="401(k) (employer + employee)" deprecated>
            <MoneyInput value={draft.annualContribution401k} onChange={v => upd('annualContribution401k', v)} />
          </Row>
          <Row label="Taxable brokerage" deprecated>
            <MoneyInput value={draft.annualContributionTaxable} onChange={v => upd('annualContributionTaxable', v)} />
          </Row>
          <Row label="HYSA" deprecated>
            <MoneyInput value={draft.annualContributionHysa} onChange={v => upd('annualContributionHysa', v)} />
          </Row>
          <Hint>
            <span className="block">
              📊 Manage per-account contribution rules on the dedicated{' '}
              <a
                href="#investments"
                className="font-semibold text-accent-700 underline-offset-2 hover:underline dark:text-accent-500"
              >
                Investments page →
              </a>
            </span>
            <span className="mt-1 block text-ink-400">
              Total saved per year (legacy fields): {fmtMoney(
                draft.annualContribution401k + draft.annualContributionTaxable + draft.annualContributionHysa,
              )}
            </span>
          </Hint>
        </Card>

        <Card title="Returns (real, after inflation)">
          <Row label="Stock real return">
            <PctInput value={draft.stockRealReturn} onChange={v => upd('stockRealReturn', v)} />
          </Row>
          <Row label="HYSA real return">
            <PctInput value={draft.hysaRealReturn} onChange={v => upd('hysaRealReturn', v)} />
          </Row>
          <Row label="Home real appreciation">
            <PctInput value={draft.homeRealAppreciation} onChange={v => upd('homeRealAppreciation', v)} />
          </Row>
          <Row
            label={
              <>
                <GlossaryTerm term="swr">Safe withdrawal rate (SWR)</GlossaryTerm>
              </>
            }
          >
            <PctInput value={draft.swr} onChange={v => upd('swr', v)} />
          </Row>
          <Row
            label={
              <>
                <GlossaryTerm term="real_return">Inflation</GlossaryTerm> (reference)
              </>
            }
          >
            <PctInput value={draft.inflation} onChange={v => upd('inflation', v)} />
          </Row>
          <Hint>6% real ≈ 9% nominal − 3% inflation. SWR 3.25% is tuned for 45-year retirement.</Hint>
        </Card>

        <Card title="Mortgage">
          <Row label="Annual interest rate">
            <PctInput value={draft.mortgageAnnualRate} onChange={v => upd('mortgageAnnualRate', v)} decimals={3} />
          </Row>
          <Row
            label={
              <>
                Monthly{' '}
                <GlossaryTerm term="mortgage_pi">P&amp;I</GlossaryTerm>
              </>
            }
          >
            <MoneyInput value={draft.mortgageMonthlyPI} onChange={v => upd('mortgageMonthlyPI', v)} />
          </Row>
          <Row
            label={
              <>
                Monthly{' '}
                <GlossaryTerm term="escrow">escrow</GlossaryTerm> (tax + insurance)
              </>
            }
          >
            <MoneyInput value={draft.mortgageMonthlyEscrow} onChange={v => upd('mortgageMonthlyEscrow', v)} />
          </Row>
          <Hint>
            Total monthly: {fmtMoney(draft.mortgageMonthlyPI + draft.mortgageMonthlyEscrow)}. Escrow persists after
            payoff.
          </Hint>
        </Card>

        <Card title="Spending">
          <Row label="Monthly household spend">
            <MoneyInput value={draft.monthlyHouseholdSpend} onChange={v => upd('monthlyHouseholdSpend', v)} />
          </Row>
          <Row label="Healthcare (annual, pre-Medicare)">
            <MoneyInput value={draft.healthcareAnnual} onChange={v => upd('healthcareAnnual', v)} />
          </Row>
          <Hint>
            Required retirement spend: {fmtMoney(required)}/yr → Required nest egg: {fmtMoney(requiredNestEgg)}
          </Hint>
        </Card>
      </main>
    </>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="card card-dark overflow-hidden">
      <div className="border-b border-ink-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-ink-500 dark:border-ink-700 dark:text-ink-300">
        {title}
      </div>
      <div className="divide-y divide-ink-100 dark:divide-ink-700">{children}</div>
    </section>
  );
}

function Row({
  label,
  children,
  deprecated,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  deprecated?: boolean;
}) {
  return (
    <div className="grid grid-cols-[1fr_45%] items-center gap-3 px-4 py-3">
      <span
        className={`text-sm ${deprecated ? 'text-ink-400 dark:text-ink-400' : 'text-ink-700 dark:text-ink-100'}`}
        title={deprecated ? DEPRECATED_TOOLTIP : undefined}
      >
        {label}
        {deprecated && (
          <span
            className="ml-1.5 inline-flex items-center rounded bg-amber-100 px-1 py-0 align-middle text-[9px] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-900/30 dark:text-amber-200"
            aria-label="deprecated"
          >
            legacy
          </span>
        )}
      </span>
      <div>{children}</div>
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-ink-50 px-4 py-2 text-[11px] text-ink-500 dark:bg-ink-800/50 dark:text-ink-300">{children}</div>
  );
}
